import { useEffect, useMemo } from 'react';
import { Provider, useDependencies } from '@servicetitan/react-ioc';
import { EVENT_BUS_TOKEN, EventBus } from '@servicetitan/web-components';
import {
  CommHubNotificationsRegistryProvider,
  CommHubNotificationsRegistryReader,
  CommHubTextNotificationsItemId,
  type CommHubNotificationsRegistryItem,
} from '@servicetitan/toolbelt-shared-registry';
import { useUnreadTextCountSetter } from '../lib/unreadTextCountContext';
import useTextingEntitled from '../hooks/useTextingEntitled';
import UnreadTextCountProducer from './UnreadTextCountProducer';

/**
 * Reads the notifications registry and pushes the count out through
 * `UnreadTextCountProvider`. Must be a *child* of the providers below --
 * `useDependencies` resolves from `useContext(ContainerContext)`, so a component
 * that renders `<Provider>` in its own JSX and calls `useDependencies` in its
 * own body would resolve against CCPro's root container, where nothing is
 * bound, and throw.
 *
 * The badge never touches the bus or the registry: the read happens here, and
 * only the count -- the genuinely shared datum -- crosses out.
 */
const UnreadTextCountBridge = () => {
  const [reader] = useDependencies(CommHubNotificationsRegistryReader);
  const setCount = useUnreadTextCountSetter();

  useEffect(() => {
    const handleRegistryChange = (
      items: Map<string, CommHubNotificationsRegistryItem>,
    ) => {
      // Deliberately no `?? 0`. A missing item means the count is unavailable,
      // which must render no badge -- not a badge reading zero.
      setCount(items.get(CommHubTextNotificationsItemId)?.count);
    };

    reader.subscribe(handleRegistryChange);

    return () => {
      reader.unsubscribe(handleRegistryChange);
      // The store outlives this bridge -- it is created once, above <Router />.
      // Leaving the last count behind would keep the badge showing a number no
      // live producer is backing any more, which is the same "unavailable must
      // not look like a real value" failure the `?? 0` above avoids. Reachable
      // without a page load: entitlement can drop in-tab when the texting flag
      // is flipped live.
      setCount(undefined);
    };
  }, [reader, setCount]);

  return <UnreadTextCountProducer />;
};

/**
 * Owns the one `EventBus` this feature needs, and composes the published
 * notifications registry provider inside it.
 *
 * The bus is sourced here rather than above `<Router />` on purpose.
 * `Loader` resolves `EVENT_BUS_TOKEN` from surrounding DI and falls back to a
 * private per-instance bus only when nothing provides one; CCPro has several
 * in-`Router` Loaders relying on that fallback, so an app-ambient bus would
 * silently join unrelated MFEs onto one bus and let them observe each other's
 * events.
 *
 * Ordering needs no manual sequencing: react-ioc's `Provider` binds and
 * `initialize()`s its `Store` singletons in its own mount effect and renders
 * `null` until that completes, so the producer cannot render before the
 * `RegistryService` is listening. That matters because `RegistryWriter.add` is a
 * bare `mitt` emit with no buffering -- a write with no listener is lost for
 * good, while a late *reader* recovers (`subscribe()` re-requests current state).
 */
const UnreadTextCountRegistryScope = () => {
  const eventBus = useMemo(() => new EventBus(), []);

  return (
    <Provider
      singletons={[{ provide: EVENT_BUS_TOKEN, useValue: eventBus }]}
      // react-ioc's defaults are a visible "Loading..." and "Error" <h2>. The
      // clip would hide them, but this feature renders nothing by contract.
      loadingFallback={null}
      errorFallback={null}
    >
      <CommHubNotificationsRegistryProvider>
        <UnreadTextCountBridge />
      </CommHubNotificationsRegistryProvider>
    </Provider>
  );
};

/**
 * App-lifetime host for the unread text-conversation count.
 *
 * The entitlement gate lives here, inside the mounted component, rather than as
 * a condition on the clip container's JSX -- the container stays a permanent,
 * logic-free wrapper. A denied user therefore leaves an empty clip element in
 * the DOM and issues no request, leases no Pusher channel and downloads no
 * bundle.
 */
const UnreadTextCountHost = () => {
  const entitled = useTextingEntitled();

  if (!entitled) {
    return null;
  }

  return <UnreadTextCountRegistryScope />;
};

export default UnreadTextCountHost;
